import React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { change, getFormValues } from 'redux-form'
import { useDispatch, useStore } from 'react-redux'
import { useMutation, useSubscription } from '@apollo/client/react'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// graphql
import { APP_NOTIFICATION } from 'graphql/notifications/subscriptions'
import { CHECK_INLINE_FUNCTIONS, CHECK_INLINE_FUNCTIONS_PUBLIC } from 'graphql/systemProcesses/mutations'
// constants
import { Forms } from 'constants/index'
// context
import { PublicContext } from 'containers/Risks/Risk/Edit/public'
// types
import { RiskNotificationType } from 'types/risk'
import { IField, IFieldLink, INotification } from 'types/graphql'
// utils
import { keyBy } from 'lodash'
import { getTableCells } from 'utils/form/risk'

type SubscriptionResponse = {
  notification: INotification
}

export type InlineFunction = {
  id: string
  name: string
  field: IField
  fieldLinkId: string
  fieldValueId: string
}

type Params = {
  riskId?: string
  riskTemplateId?: string
  fieldLinks?: IFieldLink[]
  /** imported risk form is flat and keyed by field id, its table values are set as plain fields */
  isImportedRisk?: boolean
  onRefetchRelatedForms?: () => void
}

export const useRiskNotifications = ({
  riskId,
  riskTemplateId,
  fieldLinks = [],
  isImportedRisk = false,
  onRefetchRelatedForms
}: Params) => {
  const user = useCurrentUser()
  const store = useStore()
  const router = useRouter()
  const dispatch = useDispatch()

  const isPublic = React.useContext(PublicContext)

  const { data: subscriptionData } = useSubscription<SubscriptionResponse>(APP_NOTIFICATION, {
    shouldResubscribe: true,
    skip: !riskId,
    variables: { eventType: `${riskId}:${isPublic ? router.query.domain : user.company.domainName}` }
  })

  // Fired on its own a second after the form opens: a failure is not something the user did
  const [checkInlineFunctions] = useMutation(isPublic ? CHECK_INLINE_FUNCTIONS_PUBLIC : CHECK_INLINE_FUNCTIONS, {
    context: { handledLocally: true }
  })

  const [inlineFunctions, setInlineFunctions] = React.useState<InlineFunction[]>([])

  /** Put the value into every table cell the notification points at; useSaveFieldValue of the cell persists it */
  const updateTableCells = (notification: any) => {
    const fieldLinksMap = keyBy(fieldLinks, 'id')
    const formValues = getFormValues(Forms.RISK_TEMPLATE_FORM)(store.getState())

    getTableCells(formValues, Object.keys(fieldLinksMap))
      .filter(({ fieldLinkId, cell }) => {
        const fieldLink = fieldLinksMap[fieldLinkId]

        return notification.fieldLinkId === fieldLink.id && fieldLink.primary
          ? notification.fieldValueId === cell.fieldValueId
          : notification.fieldId === fieldLink.field?.id && notification.parentFieldValueId === cell.parentFieldValueId
      })
      .forEach(({ path, cell }) =>
        dispatch(change(Forms.RISK_TEMPLATE_FORM, path, { ...cell, value: notification.fieldValue }))
      )
  }

  React.useEffect(() => {
    if (riskId && riskTemplateId) {
      setTimeout(
        () =>
          checkInlineFunctions({
            variables: {
              ...(isPublic && { domain: router.query.domain }),
              input: {
                riskId,
                riskTemplateId
              }
            }
          }),
        1000
      )
    }
  }, [riskId, riskTemplateId])

  React.useEffect(() => {
    if (subscriptionData) {
      const notification = subscriptionData?.notification?.payload

      // a field used in a ShowRelatedForm condition was saved, so the permissions may have changed
      if (notification?.notificationType === RiskNotificationType.RefetchRelatedForms) {
        onRefetchRelatedForms?.()
      }

      if (notification?.notificationType === RiskNotificationType.ShowFunction) {
        setInlineFunctions((prev) => {
          if (notification.shouldShow) {
            return [
              ...prev.filter(
                (inlineFn) =>
                  inlineFn.id !== notification.transaction.inlineFunctionId ||
                  notification.fieldValues.some((item) => item.fieldValueId === inlineFn.fieldValueId)
              ),
              ...notification.fieldValues
                .filter(
                  (item) =>
                    !prev.some(
                      (inlineFn) =>
                        inlineFn.id === notification.transaction.inlineFunctionId &&
                        inlineFn.fieldValueId === item.fieldValueId
                    )
                )
                .map((item) => ({
                  ...item,
                  id: notification.transaction.inlineFunctionId,
                  name: notification.transaction.functionName,
                  position: notification.transaction.position
                }))
            ]
          }

          return prev.filter(
            (inlineFn) =>
              inlineFn.id !== notification.transaction.inlineFunctionId ||
              notification.fieldValues.every((item) => item.fieldValueId !== inlineFn.fieldValueId)
          )
        })
      }

      if (notification?.notificationType === RiskNotificationType.SetField) {
        if (notification.fieldLinkId && !isImportedRisk) {
          updateTableCells(notification)
        } else {
          dispatch(
            change(Forms.RISK_TEMPLATE_FORM, `${isImportedRisk ? notification.field.id : notification.fieldName}`, {
              value: notification.fieldValue,
              fieldValueId: notification.fieldValueId,
              ...(isImportedRisk && {
                fieldLinkId: notification.fieldLinkId,
                parentFieldValueId: notification.parentFieldValueId
              })
            })
          )
        }
      }

      // Injury Master incident id or claim number arrived; the field is read-only, so only its value is replaced.
      if (notification?.notificationType === RiskNotificationType.FieldValueUpdated) {
        dispatch(change(Forms.RISK_TEMPLATE_FORM, `${notification.fieldId}.value`, notification.fieldValue))
      }
    }
  }, [subscriptionData])

  return { inlineFunctions }
}
